import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { Reveal } from "@/components/Reveal";

const selectCls =
  "h-11 rounded-xl bg-[#141414] border border-white/10 px-3 text-sm text-white focus:border-[#FF1E27] outline-none";

export default function Catalogue() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("");
  const [fuel, setFuel] = useState("");
  const [gearbox, setGearbox] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    api
      .get("/vehicles")
      .then((r) => setVehicles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const brands = useMemo(() => [...new Set(vehicles.map((v) => v.brand))].sort(), [vehicles]);

  const filtered = useMemo(() => {
    let list = vehicles.filter((v) => {
      const q = query.trim().toLowerCase();
      if (q && !`${v.brand} ${v.model}`.toLowerCase().includes(q)) return false;
      if (brand && v.brand !== brand) return false;
      if (fuel && v.fuel !== fuel) return false;
      if (gearbox && !v.transmission.toLowerCase().includes(gearbox)) return false;
      if (maxPrice && v.price > parseInt(maxPrice, 10)) return false;
      return true;
    });
    const sorters = {
      "price-asc": (a, b) => a.price - b.price,
      "price-desc": (a, b) => b.price - a.price,
      "km-asc": (a, b) => a.km - b.km,
      "year-desc": (a, b) => b.year - a.year,
    };
    if (sorters[sort]) list = [...list].sort(sorters[sort]);
    return list;
  }, [vehicles, query, brand, fuel, gearbox, maxPrice, sort]);

  return (
    <main data-testid="catalogue-page" className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-3">Catalogue</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight">
            Nos véhicules <span className="text-[#FF1E27]">à vendre</span>
          </h1>
          <p data-testid="catalogue-count" className="mt-3 text-sm text-zinc-400 font-spec uppercase tracking-widest">
            {filtered.length} véhicule{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
          </p>
        </Reveal>

        {/* Filtres */}
        <Reveal delay={0.1}>
          <div className="mt-10 bg-[#141414] border border-white/10 rounded-2xl p-4 sm:p-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher (marque, modèle…)"
                className={`${selectCls} w-full pl-9`}
              />
            </div>
            <select data-testid="catalog-filter-brand-select" value={brand} onChange={(e) => setBrand(e.target.value)} className={selectCls}>
              <option value="">Toutes marques</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <select data-testid="catalog-filter-fuel-select" value={fuel} onChange={(e) => setFuel(e.target.value)} className={selectCls}>
              <option value="">Toutes énergies</option>
              {["Essence", "Diesel", "Hybride", "Électrique"].map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <select data-testid="catalog-filter-gearbox-select" value={gearbox} onChange={(e) => setGearbox(e.target.value)} className={selectCls}>
              <option value="">Toutes boîtes</option>
              <option value="manuelle">Manuelle</option>
              <option value="automatique">Automatique</option>
            </select>
            <div className="flex gap-3">
              <input
                data-testid="catalog-filter-price-max"
                type="number"
                min="0"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Prix max €"
                className={`${selectCls} w-full`}
              />
              <select data-testid="catalog-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls} aria-label="Trier">
                <option value="recent">Récents</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="km-asc">Km croissant</option>
                <option value="year-desc">Année récente</option>
              </select>
            </div>
          </div>
        </Reveal>

        {/* Grille */}
        {loading ? (
          <p className="mt-20 text-center text-zinc-500 font-spec uppercase tracking-widest text-sm">Chargement du showroom…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="catalogue-empty" className="mt-20 text-center">
            <SlidersHorizontal className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Aucun véhicule ne correspond à vos critères.</p>
          </div>
        ) : (
          <div data-testid="catalogue-grid" className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.1}>
                <VehicleCard v={v} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
